import { defineStore } from "pinia";
import { ref, computed } from "vue";
import WheelConfig from "@components/wheel/WheelConfig.js";
import * as Util from "@utils/Util.js";
import * as ImageUtil from "@utils/ImageUtil.js";
import { CloudFunctions } from "@services/firebase";
import { i18n } from "@/i18n-setup.js";

// Tiny helper to translate safely even during early boot
function t(key, params) {
  try {
    return i18n && i18n.global && typeof i18n.global.t === "function"
      ? i18n.global.t(key, params)
      : key;
  } catch {
    return key;
  }
}

function saveWheelToLocalStorage(wheelConfig) {
  localStorage.setItem("LastWheelConfig", wheelConfig.getJson());
}

export const useWheelStore = defineStore("wheel", () => {
  // State
  const wheelConfig = ref(
    (() => {
      const wc = new WheelConfig();
      wc.entries = Util.addIdsIfNotThere([{ text: " " }]);
      wc.title = "";
      return wc;
    })()
  );
  const sheetLinked = ref(false);
  const wheelIsBusy = ref(false);
  const winners = ref([]);
  const path = ref("");
  const copyable = ref(false);

  // Init status
  const initStatus = ref("idle"); // 'idle' | 'loading' | 'ready' | 'error'
  const initError = ref(null);
  const _initRequestKey = ref(null);
  const _initPromise = ref(null);

  // Getters
  const wheelType = computed(() => wheelConfig.value.type);
  const entries = computed(() => wheelConfig.value.entries);
  const entryCount = computed(() => wheelConfig.value.entries.length);
  const winnerCount = computed(() => winners.value.length);
  const wheelIsShared = computed(() => !!path.value);
  const wheelIsCopyable = computed(() => copyable.value);
  const wheelIsAdvanced = computed(() => wheelConfig.value.isAdvanced);
  const wheelTitle = computed(() => wheelConfig.value.title);

  const wheelInitStatus = computed(() => initStatus.value);
  const wheelIsReady = computed(() => initStatus.value === "ready");
  const wheelIsLoading = computed(() => initStatus.value === "loading");
  const wheelInitError = computed(() => initError.value);

  // Actions/Mutators
  function setWheelConfig(newWheelConfig) {
    const newConfig = new WheelConfig();
    newConfig.loadValues(newWheelConfig);
    newConfig.entries = Util.addIdsIfNotThere(newConfig.entries);
    wheelConfig.value = newConfig;
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setGrayEmptyWheel() {
    const clone = wheelConfig.value.clone();
    clone.entries = Util.addIdsIfNotThere([{ text: " " }]);
    clone.title = "";
    wheelConfig.value = clone;
  }

  function setWheelTitle(title) {
    const clone = wheelConfig.value.clone();
    clone.title = title;
    wheelConfig.value = clone;
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setWheelDescription(description) {
    const clone = wheelConfig.value.clone();
    clone.description = description;
    wheelConfig.value = clone;
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setShowTitle(newValue) {
    wheelConfig.value.showTitle = newValue;
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setEntries(newEntries) {
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function shuffleEntries() {
    wheelConfig.value.entries = Util.shuffleArray(wheelConfig.value.entries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function sortEntries() {
    wheelConfig.value.entries = Util.sortWheelEntries(
      wheelConfig.value.entries
    );
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function appendEntry(entry) {
    const newEntries = wheelConfig.value.entries.slice(0);
    newEntries.push(entry);
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setEntryText({ index, text }) {
    const newEntries = wheelConfig.value.entries.slice(0);
    newEntries[index].text = text;
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setEntryColor({ index, color }) {
    const newEntries = wheelConfig.value.entries.slice(0);
    newEntries[index].color = color;
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setEntrySound({ index, sound }) {
    const newEntries = wheelConfig.value.entries.slice(0);
    if (sound) {
      newEntries[index].sound = sound;
    } else {
      delete newEntries[index].sound;
    }
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setEntryMessage({ index, message }) {
    const newEntries = wheelConfig.value.entries.slice(0);
    if (message) {
      newEntries[index].message = message;
    } else {
      delete newEntries[index].message;
    }
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setEntryWeight({ index, weight }) {
    const newEntries = wheelConfig.value.entries.slice(0);
    newEntries[index].weight = weight;
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function refreshEntries() {
    const newEntries = wheelConfig.value.entries.slice(0);
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setTextEntries(texts) {
    const newEntries = texts.map((t) => ({ text: t }));
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function appendTextEntries(texts) {
    const newEntries = wheelConfig.value.entries.concat(
      texts.map((t) => ({ text: t }))
    );
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function removeEntryByIndex(index) {
    const newEntries = wheelConfig.value.entries.slice(0);
    newEntries.splice(index, 1);
    wheelConfig.value.entries = newEntries;
    sheetLinked.value = false;
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function removeEntry(entry) {
    const newEntries = wheelConfig.value.entries.slice(0);
    const idx = newEntries.findIndex((e) => e.id == entry.id);
    if (idx > -1) {
      newEntries.splice(idx, 1);
      wheelConfig.value.entries = newEntries;
      sheetLinked.value = false;
      if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
    }
  }

  function removeEntryAll(entry) {
    wheelConfig.value.entries = wheelConfig.value.entries.filter((e) => {
      if (e.text) {
        return e.text.trim() !== entry.text.trim();
      } else {
        return true;
      }
    });
    sheetLinked.value = false;
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function hideEntry(entry) {
    entry.enabled = false;
    // Trigger reactivity
    const newEntries = wheelConfig.value.entries.slice(0);
    wheelConfig.value.entries = newEntries;
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function moveEntry({ index, direction }) {
    const newEntries = wheelConfig.value.entries.slice(0);
    const temp = newEntries[index];
    newEntries[index] = newEntries[index + direction];
    newEntries[index + direction] = temp;
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function duplicateEntry(index) {
    const sourceEntry = wheelConfig.value.entries[index];
    const newEntry = Object.assign({}, sourceEntry);
    delete newEntry.id; // Ensure new ID is generated
    const newEntries = wheelConfig.value.entries.slice(0);
    newEntries.splice(index + 1, 0, newEntry);
    wheelConfig.value.entries = Util.addIdsIfNotThere(newEntries);
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function removeImageFromEntry(index) {
    const newEntries = wheelConfig.value.entries.slice(0);
    delete newEntries[index].image;
    wheelConfig.value.entries = newEntries;
    if (!path.value) saveWheelToLocalStorage(wheelConfig.value);
  }

  function setWheelBusy(busy) {
    wheelIsBusy.value = busy;
  }

  function linkSheet() {
    sheetLinked.value = true;
  }

  function unlinkSheet() {
    sheetLinked.value = false;
  }

  function setPath(newPath) {
    path.value = newPath;
  }

  function setUnshared() {
    path.value = "";
    saveWheelToLocalStorage(wheelConfig.value);
  }

  function setCopyable(payload) {
    const { copyable: c, editable: e } = payload;
    if (typeof e !== "undefined") {
      copyable.value = e;
    }
    if (typeof c !== "undefined") {
      copyable.value = c;
    }
  }

  function addWinner(winner) {
    winners.value.push(winner);
  }

  function sortWinners() {
    winners.value = Util.sortWheelEntries(winners.value);
  }

  function clearWinners() {
    winners.value = [];
  }

  function setVolumes({ duringSpinSoundVolume, afterSpinSoundVolume }) {
    wheelConfig.value.duringSpinSoundVolume = duringSpinSoundVolume;
    wheelConfig.value.afterSpinSoundVolume = afterSpinSoundVolume;
  }

  function setWheelInitStatus(status) {
    initStatus.value = status;
  }

  function setWheelInitError(err) {
    initError.value = err || null;
  }

  // Actions
  async function loadInitialWheel(sharedWheelPath) {
    const winnerMessage = t("common.We have a winner!");
    const requestKey = sharedWheelPath || "default";

    if (_initPromise.value && _initRequestKey.value === requestKey) {
      return _initPromise.value;
    }

    _initRequestKey.value = requestKey;
    setWheelInitStatus("loading");
    setWheelInitError(null);
    setGrayEmptyWheel();

    const initPromise = (async () => {
      try {
        if (sharedWheelPath) {
          setPath(sharedWheelPath);

          const response = await CloudFunctions.getSharedWheel(sharedWheelPath);

          if (!response.success || !response.data) {
            let loadedFromLocal = false;
            const saved = localStorage.getItem("LastWheelConfig");
            const wc = new WheelConfig(winnerMessage);
            if (saved) {
              try {
                wc.loadJson(saved);
                loadedFromLocal = true;
              } catch {}
            }

            setWheelConfig(wc);
            setUnshared();
            setWheelInitError(`Wheel "${sharedWheelPath}" not found!`);
            setWheelInitStatus(loadedFromLocal ? "ready" : "error");
            return loadedFromLocal;
          } else {
            const result = response.data;
            setCopyable({
              copyable: result.copyable,
              editable: result.editable,
            });

            CloudFunctions.logSharedWheelRead(sharedWheelPath).catch((e) =>
              console.warn("Failed to log shared wheel read", e)
            );

            const wc = new WheelConfig(winnerMessage);
            wc.loadValues(result.config);
            setWheelConfig(wc);
            setWheelInitStatus("ready");
            return true;
          }
        } else {
          const wc = new WheelConfig(winnerMessage);
          try {
            wc.loadJson(localStorage.getItem("LastWheelConfig"));
          } catch {
            // ignore
          }
          setWheelConfig(wc);
          setWheelInitStatus("ready");
          return true;
        }
      } catch (ex) {
        let loadedFromLocal = false;
        const saved = localStorage.getItem("LastWheelConfig");
        const wc = new WheelConfig(winnerMessage);
        if (saved) {
          try {
            wc.loadJson(saved);
            loadedFromLocal = true;
          } catch {}
        }
        setWheelConfig(wc);
        if (sharedWheelPath) setUnshared();

        setWheelInitError(ex && ex.toString ? ex.toString() : String(ex));
        setWheelInitStatus(loadedFromLocal ? "ready" : "error");
        return loadedFromLocal;
      } finally {
        if (_initRequestKey.value === requestKey) {
          _initPromise.value = null;
        }
      }
    })();

    _initPromise.value = initPromise;
    return initPromise;
  }

  function resetWheel() {
    const wc = new WheelConfig(t("common.We have a winner!"));
    setWheelConfig(wc);
    clearWinners();
  }

  async function appendImageEntry(imageData) {
    const newEntries = wheelConfig.value.entries.slice(0);
    let imageEntry;
    if (wheelConfig.value.isAdvanced) {
      let color = "#CCCCCC";
      const image = await ImageUtil.getImageFromData(imageData);
      const transparent = ImageUtil.topLeftIsFullyTransparent(image);
      if (!transparent) {
        color = ImageUtil.getTopLeftColor(image).slice(0, 7);
      }
      imageEntry = {
        text: "",
        image: imageData,
        color: color,
        weight: 1,
        enabled: true,
      };
    } else {
      imageEntry = { text: "", image: imageData };
    }
    newEntries.push(imageEntry);
    setEntries(newEntries);
  }

  async function attachImageToEntry({ imageData, index }) {
    const newEntries = wheelConfig.value.entries.slice(0);
    if (!newEntries[index]) {
      newEntries[index] = {
        text: "",
        weight: 1,
        enabled: true,
        color: "#CCCCCC",
      };
    }

    const image = await ImageUtil.getImageFromData(imageData);
    const transparent = ImageUtil.topLeftIsFullyTransparent(image);

    if (!transparent) {
      const color = ImageUtil.getTopLeftColor(image).slice(0, 7);
      newEntries[index].color = color;
    }

    newEntries[index].image = imageData;
    setEntries(newEntries); // This handles addIdsIfNotThere and saving
  }

  async function setAdvanced(advanced) {
    let clone = wheelConfig.value.clone();
    clone.isAdvanced = advanced;
    if (advanced) {
      const colors = clone.getCoalescedColors();
      await Promise.all(
        clone.entries.map(async (entry, i) => {
          if (entry.image) {
            try {
              const image = await ImageUtil.getImageFromData(entry.image);
              if (!ImageUtil.topLeftIsFullyTransparent(image)) {
                entry.color = ImageUtil.getTopLeftColor(image).slice(0, 7);
              } else {
                entry.color = colors[i % colors.length];
              }
            } catch (e) {
              console.warn("Error analyzing image for color", e);
              entry.color = colors[i % colors.length];
            }
          } else {
            entry.color = colors[i % colors.length];
          }
          entry.weight = 1;
          entry.enabled = true;
        })
      );
    } else {
      const simplifiedEntries = clone.entries
        .map((entry) => {
          const newEntry = { text: entry.text };
          if (entry.image) newEntry.image = entry.image;
          return newEntry;
        })
        .filter((entry) => entry.text || entry.image);

      clone.entries = simplifiedEntries;
    }
    setWheelConfig(clone);
  }

  return {
    // State
    wheelConfig,
    sheetLinked,
    wheelIsBusy,
    winners,
    path,
    copyable,
    initStatus,
    initError,

    // Getters
    wheelType,
    entries,
    entryCount,
    winnerCount,
    wheelIsShared,
    wheelIsCopyable,
    wheelIsAdvanced,
    wheelTitle,
    wheelInitStatus,
    wheelIsReady,
    wheelIsLoading,
    wheelInitError,

    // Actions
    setWheelConfig,
    setGrayEmptyWheel,
    setWheelTitle,
    setWheelDescription,
    setShowTitle,
    setEntries,
    shuffleEntries,
    sortEntries,
    appendEntry,
    setEntryText,
    setEntryColor,
    setEntrySound,
    setEntryMessage,
    setEntryWeight,
    refreshEntries,
    setTextEntries,
    appendTextEntries,
    removeEntryByIndex,
    removeEntry,
    removeEntryAll,
    hideEntry,
    moveEntry,
    duplicateEntry,
    removeImageFromEntry,
    setWheelBusy,
    linkSheet,
    unlinkSheet,
    setPath,
    setUnshared,
    setCopyable,
    addWinner,
    sortWinners,
    clearWinners,
    setVolumes,
    setWheelInitStatus,
    setWheelInitError,
    loadInitialWheel,
    resetWheel,
    appendImageEntry,
    attachImageToEntry,
    setAdvanced,
  };
});
